import { endOfDay } from 'date-fns/endOfDay';
import { getUnixTime } from 'date-fns/getUnixTime';
import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { populateRelationshipFromRequest } from 'src/utils/populateRelationship';

import { Client, fetchClients } from '../../models/client/client';
import { getTagString } from '../../models/client/utils';
import { normalizeFbmResource } from '../../models/fbm-resource/normalizers';
import { PaymentPlan } from '../payment/_types';
import { Service } from '../service/_types';
import { User } from '../user/_types';
import { fetchUser, fetchUsers } from '../user/user';
import { normalizeUserPackage } from '../user-package/normalizers';
import { UserPackage } from '../user-package/user-package';

import {
  denormalizeScheduledMembership,
  denormalizeVisit,
  normalizeScheduledAppointment,
  normalizeScheduledMembership,
  normalizeVisit,
} from './normalizers';

import type {
  ScheduledMembership,
  Visit,
  VisitStatus,
} from './_types';
import type { ScheduledAppointment } from 'src/models/scheduled-appointment/_types';


interface FetchVisitsOptions {
  userId?: User['id'];
  withUser: boolean;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  search?: string;
  page?: number;
  perPage?: number;
  withPackage?: boolean;
  includeClients?: boolean;
  filters?: {
    appointmentId?: Identifier;
    locationId?: number | string | null;
    trainerId?: User['id'];
    serviceId?: Service['id'];
    completedVisits?: boolean;
    checkIns?: boolean;
    paidStatus?: 'paid' | 'not_paid' | 'not_charged';
    status?: VisitStatus[];
    allVisits?: boolean;
    membershipId?: number;
    dateRange?: {
      start: string;
      end: string;
    };
    lifecycleStage?: string;
    noAccount?: boolean;
    searchBy?: string;
    userPackageId?: number;
  };
}

interface FetchVisitsResponse {
  visits: Visit[];
  total: number;
}

export const fetchVisits = async ({
  userId,
  sortBy = 'completed_at',
  sortDir = 'desc',
  search,
  page = 1,
  perPage = 20,
  includeClients,
  filters = {},
}: Omit<
  FetchVisitsOptions,
  'withUser' | 'withPackage'
>): Promise<FetchVisitsResponse> => {
  const searchBy =
    filters.searchBy ||
    'user_formatted_name_or_user_email_or_appointment_service_name';
  const params = {
    page,
    per: perPage,
    for_user: true,
    'q[s]': `${sortBy} ${sortDir}`,
    ...(search && {
      [`q[${searchBy}_cont]`]: search.trim(),
    }),
    ...(userId && { 'q[user_id_or_chaperone_user_id_eq]': userId }),
    ...(filters.appointmentId && {
      'q[appointment_id_eq]': filters.appointmentId,
    }),
    ...(filters.completedVisits && { active_visits: true }),
    ...(filters.membershipId && {
      'q[membership_id_eq]': filters.membershipId,
    }),
    ...(filters.allVisits
      ? {}
      : filters.checkIns
        ? { 'q[check_in_not_null]': 1 }
        : { 'q[check_in_null]': 1 }),
    ...(filters.dateRange && {
      start_date: getUnixTime(new Date(filters.dateRange.start)),
      end_date: getUnixTime(endOfDay(new Date(filters.dateRange.end))),
    }),
    ...(filters.userPackageId && {
      'q[paid_by_package_id_eq]': filters.userPackageId,
    }),
    ...(filters.locationId && { 'q[has_location]': filters.locationId }),
    ...(filters.trainerId && { 'q[has_trainer]': filters.trainerId }),
    ...(filters.serviceId && {
      'q[appointment_service_id_eq]': filters.serviceId,
    }),
    ...(filters.status?.length
      ? {
          'q[has_status]': Array.isArray(filters.status)
            ? filters.status.join(',')
            : filters.status,
        }
      : {}),
    ...(filters.paidStatus
      ? {
          'q[cancelled_at_or_noshow_at_or_completed_at_not_null]': 1,
          ...(filters.paidStatus === 'paid'
            ? {
                'q[paid_true]': 1,
                'q[paid_by_package_id_not_null]': 1,
              }
            : filters.paidStatus === 'not_paid'
              ? { 'q[paid_false]': 1 }
              : filters.paidStatus === 'not_charged'
                ? {
                    'q[paid_true]': 1,
                    'q[paid_by_package_id_null]': 1,
                  }
                : {}),
        }
      : {}),
    ...(filters.lifecycleStage && {
      'q[has_lifecycle_stage]': filters.lifecycleStage,
    }),
    ...(filters.noAccount && { 'q[user_id_null]': 1 }),
  };

  const response = await api.get(`/api/v4/fbm/visits`, { params });

  let visits = response.data.visit?.map((v) => normalizeVisit(v)) || [];
  const total = response.data.meta.total;

  // Fetch and attach client data if requested
  if (includeClients && visits.length > 0) {
    const clientIds = [
      ...new Set(visits.map((v) => v.clientId).filter(Boolean)),
    ] as Identifier[];

    if (clientIds.length > 0) {
      const { clients } = await fetchClients({ ids: clientIds });
      const clientMap = new Map<Identifier, Client>(
        (clients || []).map((c) => [c.id, c])
      );

      visits = visits.map((visit) => {
        const client = visit.clientId
          ? clientMap.get(visit.clientId)
          : undefined;
        return {
          ...visit,
          client,
          tagString: client ? getTagString(client) : undefined,
        };
      });
    }
  }

  return { visits, total };
};

interface HydrateVisitsOptions {
  visits: Visit[];
  withUser?: boolean;
  withPackage?: boolean;
}

export const hydrateVisits = async ({
  visits,
  withUser = true,
  withPackage = false,
}: HydrateVisitsOptions): Promise<Visit[]> => {
  if (!visits || visits.length === 0) {
    return visits;
  }

  // Extract user IDs for parallel fetching
  const userIds: User['id'][] = [
    ...new Set(
      visits.map((visit) => visit.userId).filter(Boolean) as User['id'][]
    ),
  ];

  // Fetch users, first visits, and packages in parallel
  const [usersMap, firstVisits, packagesMap] = await Promise.all([
    // Fetch all users at once
    withUser && userIds.length > 0
      ? (async () => {
          const users = await fetchUsers({
            ids: userIds,
            withAlerts: true,
            withDependents: true,
          });
          return new Map(users.map((user) => [user.id, user]));
        })()
      : Promise.resolve(new Map()),
    // Fetch first visits for all users
    withUser && userIds.length > 0
      ? fetchFirstVisits(userIds)
      : Promise.resolve({}),
    // Fetch packages for all visits if needed
    withPackage
      ? (async () => {
          const packages = await Promise.all(
            visits.map((visit) => fetchVisitPackage(visit.id).catch(() => null))
          );
          return new Map(
            visits.map((visit, index) => [visit.id, packages[index]])
          );
        })()
      : Promise.resolve(new Map()),
  ]);

  // Combine all the data
  return visits.map((visit) => ({
    ...visit,
    ...(withUser && {
      user: visit.userId ? usersMap.get(visit.userId) : undefined,
    }),
    ...(withUser &&
      visit.userId && { firstVisit: firstVisits[visit.userId] === visit.id }),
    ...(withPackage && { package: packagesMap.get(visit.id) }),
  }));
};

interface FetchVisitOptions {
  withUser: boolean;
}

export const fetchVisit = async (
  id: Visit['id'],
  { withUser }: FetchVisitOptions
): Promise<Visit> => {
  const response = await api.get(`/api/v4/fbm/visits/${id}`);
  const visit = normalizeVisit(response.data);
  if (!withUser || !visit.userId) {
    return visit;
  }
  const user = await fetchUser({
    id: visit.userId,
    withAlerts: true,
    withDependents: true,
  });
  return {
    ...visit,
    user,
  };
};

export const fetchResources = async (visit: Visit) => {
  const response = await api.get(`/api/v4/fbm/resources`, {
    params: { visit_id: visit.id, fetch_all: true },
  });

  return response.data.map((r) => normalizeFbmResource(r));
};

export interface UserPackageWithPaymentPlan extends UserPackage {
  paymentPlan?: PaymentPlan; // You can specify a more precise type for the payment plan
}

export const fetchVisitPackage = async (
  id: Visit['id']
): Promise<UserPackageWithPaymentPlan> => {
  const response = await api.get(`/api/v4/fbm/visits/${id}/package`, {
    params: { include_plan: true },
  });
  const pkg: UserPackageWithPaymentPlan = normalizeUserPackage(
    response.data.user_package || response.data
  );
  if (response.data.payment_plan) {
    const plan = camelcaseKeys(response.data.payment_plan, { deep: true });
    pkg.paymentPlan = plan;
  }
  return pkg;
};

export const resetVisit = async (id: Visit['id']): Promise<void> => {
  const response = await api.put(`/api/v4/fbm/visits/${id}/reset`);
  return response.data;
};

export const createVisit = async (visit: Partial<Visit>): Promise<Visit> => {
  const response = await api.post(
    `/api/v4/fbm/visits/`,
    denormalizeVisit(visit)
  );

  return normalizeVisit(response.data);
};

export const updateVisit = async (
  visit: Partial<Visit> & {
    cancelRecurring?: boolean;
  }
): Promise<Visit> => {
  const response = await api.put(`/api/v4/fbm/visits/${visit.id}`, {
    visit: denormalizeVisit(visit),
  });

  return normalizeVisit(response.data);
};

export const deleteVisit = async (id: Visit['id']): Promise<Visit> => {
  const response = await api.delete(`/api/v4/fbm/visits/${id}`);
  return normalizeVisit(response.data);
};

export type BulkChargeResponse = {
  status: 'pending' | 'success';
  visit?: Visit;
};

export const bulkChargeDefaultPackage = async ({
  visitIds,
  completeVisit,
}: {
  visitIds: Visit['id'][];
  completeVisit: boolean;
}): Promise<BulkChargeResponse> => {
  try {
    const response = await api.post(`/api/v4/fbm/visits/bulk_charge`, {
      visit_ids: visitIds,
      complete_visit: completeVisit,
    });
    return {
      status: response.data.status || 'success',
      visit: response.data.id ? normalizeVisit(response.data) : undefined,
    };
  } catch (error: any) {
    throw new Error(api.formatError(error, 'Error bulk charging visits.'));
  }
};

export const bulkUpdateVisits = async ({
  visitIds,
  status,
  cancelRecurring,
}: {
  visitIds: Visit['id'][];
  status: VisitStatus;
  cancelRecurring: boolean;
}): Promise<Visit> => {
  const response = await api.post(`/api/v4/fbm/visits/bulk_update`, {
    visit_ids: visitIds,
    status: status,
    cancel_recurring: cancelRecurring,
  });
  return normalizeVisit(response.data);
};

interface FetchFirstVisitsResponse {
  [userId: User['id']]: Visit['id'];
}

// Add this new function to fetch first visits
export const fetchFirstVisits = async (
  userIds: User['id'][]
): Promise<FetchFirstVisitsResponse> => {
  const response = await api.get('/api/v4/fbm/visits/first_visit', {
    params: { user_ids: userIds.join(',') },
  });

  return response.data;
};

export const fetchVisitAvailablePackages = async (
  visitId: Visit['id']
): Promise<UserPackage[]> => {
  const response = await api.get(
    `/api/v4/fbm/visits/${visitId}/available_packages`
  );

  return response.data.map((pkg: any) => normalizeUserPackage(pkg));
};

interface FetchScheduledMembershipsOptions {
  userId?: User['id'];
  scheduledAppointmentId?: Identifier;
  appointmentId?: Identifier;
  page?: number;
  perPage?: number;
  fetchAll?: boolean;
  search?: string;
  serviceId?: Service['id'];
}

export const fetchScheduledMemberships = async ({
  userId,
  scheduledAppointmentId,
  appointmentId,
  fetchAll,
  page,
  perPage,
  search,
  serviceId,
}: FetchScheduledMembershipsOptions): Promise<{
  total: number;
  data: ScheduledMembership[];
}> => {
  const data: Record<string, unknown> = {
    fetch_all: fetchAll,
    page: page,
    per: perPage,
  };

  // Filter by user
  if (userId) {
    data['q[user_id_eq]'] = userId;
  }

  // Filter by scheduled appointment ID (for recurring session view)
  if (scheduledAppointmentId) {
    data.scheduled_appointment_id = scheduledAppointmentId;
  }

  // Filter by appointment ID (for filtering out deleted visits)
  if (appointmentId) {
    data.appointment_id = appointmentId;
  }

  // Filter by service
  if (serviceId) {
    data['q[scheduled_appointment_service_id_eq]'] = serviceId;
  }

  if (search) {
    data['q[user_formatted_name_or_user_email_cont]'] = search;
  }

  const response = await api.get('/api/v4/fbm/scheduled_appointment_members', {
    params: data,
  });

  // API returns array directly when using scheduled_appointment_id filter
  const membershipsData =
    response.data.scheduled_appointment_member || response.data;
  const memberships = (
    Array.isArray(membershipsData) ? membershipsData : []
  ).map((membership: any) => normalizeScheduledMembership(membership));
  const scheduledAppointments = await populateRelationshipFromRequest({
    list: memberships,
    idKey: 'scheduledAppointmentId',
    newKey: 'scheduledAppointment',
    func: (ids) => fetchScheduledAppointmentWithIds(ids),
  });

  return {
    total: response.data.meta?.total || memberships.length,
    data: scheduledAppointments,
  };
};

export const fetchScheduledAppointmentWithIds = async (
  ids: User['id'][]
): Promise<ScheduledAppointment[]> => {
  const response = await api.get(`/api/v4/fbm/scheduled_appointments`, {
    params: { with_ids: ids, fetch_all: true },
  });

  // The API returns an array directly in response.data when using with_ids
  const appointments = Array.isArray(response.data)
    ? response.data
    : response.data.scheduled_appointment || [response.data];

  return appointments.map((appointment: any) =>
    normalizeScheduledAppointment(appointment)
  );
};

export const fetchScheduledAppointment = async (
  id: User['id']
): Promise<ScheduledAppointment> => {
  const response = await api.get(`/api/v4/fbm/scheduled_appointments/${id}`);
  return normalizeScheduledAppointment(response.data);
};

export const deleteScheduledMembership = async (
  id: ScheduledMembership['id']
): Promise<void> => {
  await api.delete(`/api/v4/fbm/scheduled_appointment_members/${id}`);
};

export const updateScheduledMembership = async (
  id: ScheduledMembership['id'],
  membership: Partial<ScheduledMembership>
): Promise<ScheduledMembership> => {
  const response = await api.put(
    `/api/v4/fbm/scheduled_appointment_members/${id}`,
    denormalizeScheduledMembership(membership)
  );
  return normalizeScheduledMembership(response.data);
};

export interface CreateScheduledMembershipParams {
  userId: User['id'];
  scheduledAppointmentId: Identifier;
  recurringCount?: number;
  recurringType?: string;
  recurringDayNumbers?: number[];
  days?: number[];
  startAt?: Date;
  endAt?: Date | null;
}

export const createScheduledMembership = async (
  params: CreateScheduledMembershipParams
): Promise<ScheduledMembership> => {
  const payload = denormalizeScheduledMembership({
    userId: params.userId,
    scheduledAppointmentId: params.scheduledAppointmentId,
    recurringCount: params.recurringCount || 1,
    recurringType: params.recurringType || 'week',
    recurringDayNumbers: params.recurringDayNumbers || [],
    days: params.days || [],
    startAt: params.startAt || new Date(),
    endAt: params.endAt || null,
  } as Partial<ScheduledMembership>);

  const response = await api.post(
    '/api/v4/fbm/scheduled_appointment_members',
    payload
  );
  return normalizeScheduledMembership(response.data);
};
